import React from 'react';
import { motion as Motion } from 'framer-motion';
import { CheckCircle2, X } from 'lucide-react';

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const StakesSection = React.memo(({ t, color }) => (
  <section id="learn-more" className="py-[20vw] md:py-[4vw] bg-surface-color/10">
    <div className="max-w-[1800px] mx-auto px-10 md:px-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">

      {/* Benefits card */}
      <Motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={staggerContainer}
        className="space-y-8 p-8 md:p-16 rounded-[4px] text-white"
        style={{ backgroundColor: color }}
      >
        <Motion.div variants={fadeInUp} className="flex items-center gap-4 mb-4">
          <div className="w-8 h-px bg-white/50" />
          <h6 className="text-[10px] md:text-xs font-bold uppercase tracking-[0.4em] text-white/70">{t.benefitsLabel}</h6>
        </Motion.div>
        <Motion.h4 variants={fadeInUp} className="block font-black leading-[0.95] uppercase tracking-tight text-white [text-shadow:none]">
          {t.benefitsTitle[0]}
        </Motion.h4>
        <Motion.h3 variants={fadeInUp} className="font-black leading-[0.95] md:leading-[0.8] uppercase tracking-tight [text-shadow:none] audiowide-regular text-white mb-8 -mt-6">
          {t.benefitsTitle[1]}
        </Motion.h3>
        <Motion.p variants={fadeInUp} className="text-[clamp(1rem,2vw,1.25rem)] text-white/80 font-light leading-relaxed">
          {t.benefitsText}
        </Motion.p>
        <Motion.div variants={fadeInUp} className="flex flex-wrap gap-3 pt-4">
          {t.benefitsCheckList.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
              <CheckCircle2 size={16} className="text-white" />
              <span className="text-[10px] md:text-sm font-bold uppercase tracking-widest text-white">{item}</span>
            </div>
          ))}
        </Motion.div>
      </Motion.div>

      {/* Loss card */}
      <Motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={staggerContainer}
        className="relative space-y-8 p-8 md:p-16 rounded-[4px] text-white overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 40%, #be123c 100%)' }}
      >
        <div
          className="pointer-events-none absolute inset-0 rounded-[4px]"
          style={{ background: 'radial-gradient(ellipse at 20% 30%, rgba(255,255,255,0.10) 0%, transparent 60%)' }}
        />
        <Motion.div variants={fadeInUp} className="relative flex items-center gap-4 mb-4">
          <div className="w-8 h-px bg-white/60" />
          <h6 className="text-[10px] md:text-xs font-bold uppercase tracking-[0.4em] text-white/80">{t.lossesLabel}</h6>
        </Motion.div>
        <Motion.h4 variants={fadeInUp} className="relative block font-black leading-[0.95] uppercase tracking-tight text-white [text-shadow:none]">
          {t.lossesTitle[0]}
        </Motion.h4>
        <Motion.h3 variants={fadeInUp} className="relative font-black leading-[0.95] md:leading-[0.8] uppercase tracking-tight [text-shadow:none] audiowide-regular text-white mb-8 -mt-6">
          {t.lossesTitle[1]}
        </Motion.h3>
        <Motion.p variants={fadeInUp} className="relative text-[clamp(1rem,2vw,1.25rem)] text-white/85 font-light leading-relaxed">
          {t.lossesText}
        </Motion.p>
        <Motion.div variants={fadeInUp} className="relative flex flex-wrap gap-3 pt-4">
          {t.lossesCheckList.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white/15 rounded-full border border-white/30 backdrop-blur-sm">
              <X size={16} className="text-white" />
              <span className="text-[10px] md:text-sm font-bold uppercase tracking-widest text-white">{item}</span>
            </div>
          ))}
        </Motion.div>
      </Motion.div>

    </div>
  </section>
));

export default StakesSection;
